import { schoolLocale } from '@mirinae/shared/il8n/school';

/*
 *    Small rules the school screens share: the admin app's school section (#14) and the university app's
 *    teacher and student pages (#16, #17). Here rather than imported across apps, so neither bundle pulls
 *    in the other's pages.
 */

// A date as a calendar day in the school's time zone. `end` is for the exclusive ends the server stores
// (a licence's endDate, a class's termEnd: the instant the day after the last begins), so they show the
// last day itself. Written the school language's way ("Oct 14, 2026", "2026년 10월 14일").
export const day = (date, timeZone, { end = false } = {}) => {
    if (!date) return '';
    const at = new Date(new Date(date).getTime() - (end ? 1 : 0));
    const locale = schoolLocale();
    try {
        return at.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric', timeZone });
    } catch (e) {
        // an unknown zone name throws; the viewer's own zone is better than nothing
        return at.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
    }
};

// A moment as a day and time in the school's time zone ("Oct 16, 2026, 09:05"), for the progress screens and
// "figures as of" (#32 A-2): the school's clock, not the viewer's.
export const dayTime = (date, timeZone) => {
    if (!date) return '';
    const locale = schoolLocale();
    const parts = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false };
    try {
        return new Date(date).toLocaleString(locale, { ...parts, timeZone });
    } catch (e) {
        return new Date(date).toLocaleString(locale, parts);
    }
};

// How many of the school's calendar days ago a moment was: 0 today, 1 yesterday (#32 A-2).
export const schoolDaysAgo = (date, timeZone, now = new Date()) => {
    const ymd = (d) => {
        try {
            return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
        } catch (e) {
            return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
        }
    };
    return Math.round((Date.parse(ymd(now)) - Date.parse(ymd(new Date(date)))) / 86400000);
};

// Where a class is in its life: archived, or by its dates upcoming, ended or running. A class without
// dates (made before they were required) counts as running.
export const statusOf = (c, now = new Date()) => {
    if (c.status === 'archived') return 'archived';
    if (c.termStart && new Date(c.termStart) > now) return 'upcoming';
    if (c.termEnd && new Date(c.termEnd) <= now) return 'ended';
    return 'running';
};

// Notice titles, texts and comments are stored escaped -- the server turns & < > " ' into entities before
// saving (H2, sanitiseText) -- so a page shows them as plain text by turning the five back. React escapes
// what it renders, so this never becomes markup; the server escapes again whatever is saved.
const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
export const storedText = (value) => String(value ?? '').replace(/&(amp|lt|gt|quot|#39);/g, m => ENTITIES[m]);

// The language a person reads lessons in, as the Lessons app works it out (lessons/modules/contexts/lessons.js):
// the one they saved, else the site's own. Only a lesson language counts -- the site's languages; Korean
// is not one, the lessons teach it -- so anything else falls back to the site's language, then English.
// Sent with a student's own progress so the class's lessons open in the same translation (#17, R17-2).
export const LESSON_LANGUAGES = ['en', 'ja', 'vi', 'es', 'id', 'ru', 'zh', 'fr', 'mn', 'th'];
export const lessonLanguageOf = (user, siteLanguage) =>
    [user?.preferences?.language, siteLanguage].find(l => LESSON_LANGUAGES.includes(l)) || 'en';

// The school product runs on the test sites and locally only, as the server's ORG_ENABLED (modules/define.js)
// and the admin app's Schools menu: elsewhere the /api/org routes are not there to ask.
export const ORG_SITE = typeof window !== 'undefined' && /^(alpha|seoul|local)/.test(window.location.hostname);

// Where a school's class page is, for someone with this role in it (#17).
export const classPagePath = (alias, orgRole, classId) =>
    `/university/${alias}/${orgRole === 'student' ? 'student' : 'teacher'}/classes/${classId}`;

// The class picked last, per account, so moving between the apps (other bundles, full page loads) keeps it
// (design review 3.1, plan §9-6b). In this tab only (sessionStorage): a shared computer's next person, or a
// new session, starts fresh. Whoever reads it checks it against the person's current classes first.
//
// Picking also tells the page at once (a window event), whether or not storage works, so the header and
// the Lessons class card follow each other without a reload. Picking the same class again says nothing.
const pickedKey = userId => `mirinae-class:${userId}`;
const PICKED_EVENT = 'mirinae-class-picked';
const inMemory = new Map();   // when storage is blocked, this page still remembers
export const pickedClass = (userId) => {
    try {
        const stored = JSON.parse(window.sessionStorage.getItem(pickedKey(userId)) || 'null');
        if (stored) return stored;
    } catch (e) { /* storage blocked */ }
    return inMemory.get(String(userId)) || null;
};
export const rememberClass = (userId, orgId, classId) => {
    if (!userId || !orgId || !classId) return;
    const value = { orgId: String(orgId), classId: String(classId) };
    const before = pickedClass(userId);
    if (before && before.orgId === value.orgId && before.classId === value.classId) return;
    inMemory.set(String(userId), value);
    try { window.sessionStorage.setItem(pickedKey(userId), JSON.stringify(value)); } catch (e) { /* none */ }
    window.dispatchEvent(new CustomEvent(PICKED_EVENT, { detail: { userId: String(userId), ...value } }));
};
// Follow picks made elsewhere on this page; returns the function that stops following.
export const onClassPicked = (listener) => {
    const handler = e => listener(e.detail);
    window.addEventListener(PICKED_EVENT, handler);
    return () => window.removeEventListener(PICKED_EVENT, handler);
};

// A university page that shows one class (/university/<alias>/student|teacher/classes/<id>/...).
export const isClassPage = pathname => /^\/university\/[^/]+\/(student|teacher)\/classes\//.test(pathname || '');
